import { campaignAnalyticsGranularitySchema, campaignAnalyticsKpiKeySchema } from "@adpilot/shared";
import { IconBrandAmazon } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { DashboardDataFreshness } from "@/components/dashboard/dashboard-data-freshness";
import { DashboardOverviewSections } from "@/components/dashboard/dashboard-overview-sections";
import { DashboardFrame, DashboardHeading } from "@/components/dashboard/dashboard-page-layout";
import { WorkspaceAiPolicyDialog } from "@/components/dashboard/workspace-ai-policy/workspace-ai-policy-dialog";
import { ConnectAmazonDialog } from "@/components/settings/ConnectAmazonDialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDashboardDataFreshness } from "@/hooks/use-dashboard-data-freshness";
import { useDashboardPerformance } from "@/hooks/use-dashboard-performance";
import { useMinuteNow } from "@/hooks/use-minute-now";
import { usePermission } from "@/hooks/use-permission";
import { useShopScope } from "@/hooks/use-shop-scope";
import { fetchDashboardSummary } from "@/lib/dashboard-api";
import { resolveDashboardConnection } from "@/lib/dashboard-sales";
import { requireModule } from "@/lib/module-access";
import { canManageAmazonForActiveWorkspace } from "@/lib/workspace-access";
import { useAuthStore } from "@/stores/auth";
import { resolveReportingPeriod, useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

// AI Operations opens the same policy dialog and imports it from this route module.
export { WorkspaceAiPolicyDialog };

export const Route = createFileRoute("/_authed/")({
  beforeLoad: () => {
    requireModule("dashboard");
  },
  validateSearch: z.object({
    metric: z
      .enum([...campaignAnalyticsKpiKeySchema.options, "totalSales", "tacos"])
      .catch("spend"),
    compareMetric: z
      .enum([...campaignAnalyticsKpiKeySchema.options, "totalSales", "tacos"])
      .nullable()
      .catch("totalSales"),
    granularity: campaignAnalyticsGranularitySchema.catch("day"),
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const { t } = useTranslation("dashboard");
  const { hasPermission, hasModule } = usePermission();
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [aiPolicyOpen, setAiPolicyOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const currentShop = useAuthStore((state) =>
    state.shops.find((shop) => shop.shopId === state.currentShopId),
  );
  // ADP-110: the analytics scope (multi-select, default all workspace shops)
  // drives the aggregated summary; the sorted set keys the query so a scope
  // change refetches.
  const { scopeShopIds, workspaceShops } = useShopScope();
  const now = useMinuteNow();
  const resolvedPeriod = resolveReportingPeriod(range, now, timeZone);
  const frozenPeriod = { type: "custom" as const, ...resolvedPeriod };
  const dashboardScope = {
    workspaceId: user?.activeWorkspaceId ?? null,
    shopIds: scopeShopIds,
    ...resolvedPeriod,
    timeZone,
    granularity: search.granularity,
  };
  const freshnessQuery = useDashboardDataFreshness(dashboardScope);
  const { ads: analyticsQuery, sales: salesQuery } = useDashboardPerformance(dashboardScope);
  const { data, isLoading, isError, errorUpdatedAt, refetch } = useQuery({
    queryKey: [
      "dashboard",
      "summary",
      user?.activeWorkspaceId,
      scopeShopIds.join(","),
      resolvedPeriod.startDate,
      resolvedPeriod.endDate,
      timeZone,
    ],
    queryFn: () => fetchDashboardSummary(frozenPeriod, timeZone, scopeShopIds),
    enabled: scopeShopIds.length > 0,
  });

  useEffect(() => {
    if (isError && data && errorUpdatedAt > 0) toast.error(t("loadFailed"));
  }, [data, errorUpdatedAt, isError, t]);

  const [connectAmazonOpen, setConnectAmazonOpen] = useState(false);
  const handleConnect = () => setConnectAmazonOpen(true);
  // Rendered as the second child of both returns below: activating the first
  // Shop flips this page out of its empty state, and the dialog must survive
  // that switch to show its finish step.
  const connectAmazonDialog = (
    <ConnectAmazonDialog open={connectAmazonOpen} onOpenChange={setConnectAmazonOpen} />
  );

  const canConnect = canManageAmazonForActiveWorkspace(user);
  const connection = resolveDashboardConnection(data?.connected, salesQuery.data?.connected);

  if (scopeShopIds.length === 0 || connection === "connect") {
    return (
      <>
        <DashboardFrame>
          <EmptyState
            icon={<IconBrandAmazon className="size-16" />}
            title={t("connect.title")}
            description={canConnect ? t("connect.description") : t("connect.memberDescription")}
            actionLabel={canConnect ? t("connect.action") : undefined}
            onAction={canConnect ? handleConnect : undefined}
          />
        </DashboardFrame>
        {connectAmazonDialog}
      </>
    );
  }

  return (
    <>
      <DashboardFrame>
        <DashboardHeading
          aiMasterOn={data?.aiMasterOn ?? false}
          dataFreshness={
            <DashboardDataFreshness
              ads={freshnessQuery.data?.ads}
              sales={freshnessQuery.data?.sales}
              unavailable={freshnessQuery.isError}
              loading={freshnessQuery.isLoading}
              canOpenSettings={hasModule("settings")}
            />
          }
          onManageAi={data && hasPermission("manage_ai") ? () => setAiPolicyOpen(true) : undefined}
        />
        {data &&
        workspaceShops.length > 1 &&
        (data.currencyApproximate || data.excludedShops > 0) ? (
          <div className="flex shrink-0 items-center gap-2">
            {data.currencyApproximate ? (
              <span
                className="rounded-full border border-warning/30 bg-warning/10 px-2.5 py-0.5 text-[10px] font-medium text-warning-foreground"
                title={t("shopScope.approxCurrency")}
              >
                {t("shopScope.approxCurrency")}
              </span>
            ) : null}
            {data.excludedShops > 0 ? (
              <span className="rounded-full border border-line bg-surface px-2.5 py-0.5 text-[10px] text-muted-foreground">
                {t("shopScope.excludedShops", { count: data.excludedShops })}
              </span>
            ) : null}
          </div>
        ) : null}
        <DashboardOverviewSections
          analytics={analyticsQuery.data}
          salesTraffic={salesQuery.data}
          salesLoading={salesQuery.isLoading}
          salesFetching={salesQuery.isFetching}
          salesError={salesQuery.isError}
          onSalesRetry={() => void salesQuery.refetch()}
          startDate={resolvedPeriod.startDate}
          endDate={resolvedPeriod.endDate}
          metric={search.metric}
          compareMetric={search.compareMetric}
          onCompareMetricChange={(compareMetric) =>
            void navigate({ search: (previous) => ({ ...previous, compareMetric }), replace: true })
          }
          granularity={search.granularity}
          onMetricChange={(metric) =>
            void navigate({ search: (previous) => ({ ...previous, metric }), replace: true })
          }
          onGranularityChange={(granularity) =>
            void navigate({ search: (previous) => ({ ...previous, granularity }), replace: true })
          }
          isLoading={analyticsQuery.isLoading}
          isFetching={analyticsQuery.isFetching}
          isError={analyticsQuery.isError}
          onRetry={() => void analyticsQuery.refetch()}
          adsNotice={
            connection === "ads_missing" ? (
              <div
                role="status"
                className="flex items-center justify-between gap-3 text-xs text-muted-foreground"
              >
                <span className="flex items-center gap-2">
                  <IconBrandAmazon className="size-4 shrink-0" aria-hidden />
                  {canConnect ? t("connect.adsMissing") : t("connect.memberDescription")}
                </span>
                {canConnect ? (
                  <Button size="xs" variant="outline" onClick={handleConnect}>
                    {t("connect.action")}
                  </Button>
                ) : null}
              </div>
            ) : undefined
          }
          summary={data?.aiSummary}
        />
        {isError && !data ? (
          <ErrorState message={t("loadFailed")} onRetry={() => void refetch()} />
        ) : null}
        {isLoading ? <Skeleton className="h-20 w-full rounded-xl" /> : null}
        {data ? (
          <WorkspaceAiPolicyDialog
            open={aiPolicyOpen}
            onOpenChange={setAiPolicyOpen}
            shopName={currentShop?.shopName ?? t("shopFallback")}
            eligibleCampaigns={data.aiSummary.eligibleCampaigns}
            excludedCampaigns={Math.max(
              0,
              data.campaigns.length - data.aiSummary.eligibleCampaigns,
            )}
            nextRunAt={data.aiSummary.nextRunAt}
          />
        ) : null}
      </DashboardFrame>
      {connectAmazonDialog}
    </>
  );
}
